import { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import TodoItem from './TodoItem'
import AddTodoForm from './AddTodoForm'
import UpgradeBanner from './UpgradeBanner'
import CelebrationEffect from './CelebrationEffect'
import { ListTodo, Filter, GripVertical, Loader2, ArrowUpDown, Activity, Download, Calendar, BarChart3 } from 'lucide-react'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { ProjectActivityLog } from './team/ProjectActivityLog'
import CalendarView from './CalendarView'
import { GanttChartV2 } from './GanttChartV2'
import { exportTasks, type ExportFormat, formatLabels } from '../utils/exportTasks'
import { useAuth } from '../contexts/AuthContext'
import { getUserTeamRole } from '../services/teamService'
import { supabase, type DefaultView } from '../lib/supabase'
import { isNativeApp } from '../lib/platform'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  TouchSensor,
  MouseSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragMoveEvent,
  defaultDropAnimationSideEffects,
  Modifier
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import SortableTodoItem from './SortableTodoItem'

type FilterStatus = 'pending' | 'in_progress' | 'completed' | 'deleted'
type SortOrder = 'custom' | 'due_date_asc' | 'due_date_desc' | 'created_at_asc' | 'created_at_desc'

// モジュールスコープのフラグ - アプリ起動時に一度だけdefault viewをロード
let defaultViewLoadedGlobal = false

export default function TodoList() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { todos, selectedProjectId, projects, updateTodoOrder, updateTodosOrderBatch, loadMoreTodos, hasMoreTodos, loadingMore, loading, isProjectSelectorOpen, todoCountsByStatus, ganttFullscreenMode, setGanttFullscreenMode, isPersonalSectionVisible, isTeamSectionVisible, activeTab, setActiveTab } = useStore()
  const { soundEnabled } = useFeedbackSettings()
  const [celebrations, setCelebrations] = useState<Array<{ id: string; x: number; y: number }>>([])
  const celebrationIdCounter = useRef(0)
  const [userRole, setUserRole] = useState<string | null>(null)

  const triggerCelebration = (x: number, y: number) => {
    const id = `celebration-${celebrationIdCounter.current++}`
    setCelebrations(prev => [...prev, { id, x, y }])
  }

  const removeCelebration = (id: string) => {
    setCelebrations(prev => prev.filter(c => c.id !== id))
  }

  // フィルター状態をlocalStorageから復元
  const [selectedStatuses, setSelectedStatuses] = useState<FilterStatus[]>(() => {
    try {
      const saved = localStorage.getItem('todo_filter_statuses')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
        }
      }
    } catch (err) {
      console.error('Failed to load filter statuses:', err)
    }
    // デフォルト: 未着手と進行中のみ表示（完了とゴミ箱は非表示）
    return ['pending', 'in_progress']
  })

  // ソート順をlocalStorageから復元
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    try {
      const saved = localStorage.getItem('todo_sort_order')
      if (saved && ['custom', 'due_date_asc', 'due_date_desc', 'created_at_asc', 'created_at_desc'].includes(saved)) {
        return saved as SortOrder
      }
    } catch (err) {
      console.error('Failed to load sort order:', err)
    }
    return 'custom' // デフォルト: カスタム順（display_order）
  })

  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [lastOverId, setLastOverId] = useState<string | null>(null)
  const [autoScrollInterval, setAutoScrollInterval] = useState<NodeJS.Timeout | null>(null)
  const [pointerOffset, setPointerOffset] = useState({ x: 0, y: 0 })
  const initialPointerRef = useRef<{ x: number; y: number } | null>(null)
  const lastDragEndTimeRef = useRef<number>(0)  // 重複ドラッグイベント防止用
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)
  // activeTabはuseStoreから取得（再マウントしても維持される）
  const [showExportMenu, setShowExportMenu] = useState(false)
  const exportMenuRef = useRef<HTMLDivElement>(null)
  // defaultViewLoadedはモジュールスコープのグローバル変数を使用

  const selectedProject = projects.find(p => p.id === selectedProjectId)

  // エクスポートメニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false)
      }
    }
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportMenu])

  // エクスポートハンドラ
  const handleExport = (format: ExportFormat) => {
    exportTasks({
      project: selectedProject || null,
      todos: todos,
      exportFormat: format,
    })
    setShowExportMenu(false)
  }

  // Load user role for current project
  useEffect(() => {
    const loadUserRole = async () => {
      if (!user || !selectedProject) {
        setUserRole(null)
        return
      }

      if (!selectedProject.team_id) {
        // Not a team project, user has full access
        setUserRole('owner')
        return
      }

      // Get user's role in the team
      const role = await getUserTeamRole(selectedProject.team_id, user.id)
      setUserRole(role)
    }

    loadUserRole()
  }, [user, selectedProject])

  // Check if user is viewer (read-only access)
  const isViewer = userRole === 'viewer'

  // プロジェクトが切り替わってもタブはそのまま維持
  // （以前は tasks に戻していたが、カレンダー・ガントビュー時に維持するよう変更）

  // Load default view from database on initial load
  // モジュールスコープのグローバルフラグを使用し、アプリ起動時に一度だけロード
  useEffect(() => {
    const loadDefaultView = async () => {
      // グローバルフラグで一度だけ実行
      if (defaultViewLoadedGlobal || !user) return
      defaultViewLoadedGlobal = true

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('default_view')
          .eq('id', user.id)
          .single()

        if (!error && data?.default_view) {
          const view = data.default_view as DefaultView
          if (['tasks', 'calendar', 'gantt'].includes(view)) {
            const currentStoreTab = useStore.getState().activeTab
            // DBの値がZustandと同じなら更新不要
            if (currentStoreTab !== view) {
              setActiveTab(view as 'tasks' | 'calendar' | 'gantt')
            }
            // Also save to localStorage for faster subsequent loads
            localStorage.setItem('defaultView', view)
          }
        }
      } catch (err) {
        console.error('Failed to load default view:', err)
      }
    }

    loadDefaultView()
  }, [user])

  // タブ切り替えハンドラ（ガント以外に切り替える時はフルスクリーンをリセット）
  const handleTabChange = (newTab: 'tasks' | 'activity' | 'calendar' | 'gantt') => {
    if (newTab !== 'gantt' && ganttFullscreenMode !== 'none') {
      setGanttFullscreenMode('none')
    }
    setActiveTab(newTab)
  }

  // キーボードショートカット
  useKeyboardShortcuts({
    enabled: !isProjectSelectorOpen, // プロジェクト選択画面が開いている時は無効
    shortcuts: [
      {
        key: 'n',
        description: '新しいタスクを作成',
        action: () => {
          // AddTodoFormのボタンをクリック
          const addButton = document.querySelector('[data-add-todo-button]') as HTMLButtonElement
          if (addButton) {
            addButton.click()
          }
        },
      },
      {
        key: 'f',
        ctrl: true,
        description: 'フィルターメニューを開く',
        action: () => {
          setShowFilterMenu(prev => !prev)
        },
      },
    ],
  })

  // フィルター状態が変更されたらlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem('todo_filter_statuses', JSON.stringify(selectedStatuses))
    } catch (err) {
      console.error('Failed to save filter statuses:', err)
    }
  }, [selectedStatuses])

  // ソート順が変更されたらlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem('todo_sort_order', sortOrder)
    } catch (err) {
      console.error('Failed to save sort order:', err)
    }
  }, [sortOrder])

  // フィルターメニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterMenu && !(event.target as Element).closest('.relative')) {
        setShowFilterMenu(false)
      }
      if (showSortMenu && !(event.target as Element).closest('.relative')) {
        setShowSortMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showFilterMenu, showSortMenu])

  // ドラッグ終了時のクリーンアップ
  const cleanupDrag = () => {
    try {
      document.body.classList.remove('dragging')
    } catch (err) {
      console.error('Failed to cleanup drag:', err)
    }
  }

  // 自動スクロールを確実に停止する関数
  const stopAutoScroll = () => {
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval)
      setAutoScrollInterval(null)
    }
  }

  // AudioContextの初期化
  useEffect(() => {
    initializeFeedback()
  }, [])

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      stopAutoScroll()
      cleanupDrag()
    }
  }, [])

  // ドラッグ状態の監視とクリーンアップ
  useEffect(() => {
    if (!activeId) {
      // ドラッグが終了したら必ずクリーンアップ
      stopAutoScroll()
      cleanupDrag()
    }
  }, [activeId])

  // カスタムモディファイア：DragOverlayを指の位置に追従させる
  const cursorOffsetModifier: Modifier = ({
    transform,
  }) => {
    // 初期位置との差分を計算して、要素を指の位置に配置
    if (initialPointerRef.current) {
      return {
        ...transform,
        x: transform.x + pointerOffset.x,
        y: transform.y + pointerOffset.y,
      };
    }

    return transform;
  };

  // Safari対策：TouchSensorとMouseSensorを分けて使用
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,  // 反応性とスクロール判定のバランス
        tolerance: 25,  // Safari用：画面上部でもドラッグを保持するため大きめの許容値
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const filteredAndSortedTodos = useMemo(() => {
    let filtered = selectedProjectId
      ? todos.filter(todo => todo.project_id === selectedProjectId)
      : todos

    // 「全プロジェクト」選択時：セクション表示状態に応じてフィルタリング
    if (!selectedProjectId) {
      filtered = filtered.filter(todo => {
        const project = projects.find(p => p.id === todo.project_id)
        if (!project) return true // プロジェクトが見つからない場合は表示

        // チームプロジェクトの判定
        const isTeamProject = project.is_team_project && project.team_id

        if (isTeamProject) {
          // チームプロジェクトのタスク → チームセクション表示状態でフィルタリング
          return isTeamSectionVisible
        } else {
          // 個人プロジェクトのタスク → 個人セクション表示状態でフィルタリング
          return isPersonalSectionVisible
        }
      })
    }

    // 選択されたステータスでフィルタリング
    filtered = filtered.filter(todo => selectedStatuses.includes(todo.status))

    // ソート順に応じて並び替え
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOrder) {
        case 'due_date_asc':
          // 期限が近い順（nullは最後）
          if (!a.due_date) return 1
          if (!b.due_date) return -1
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime()

        case 'due_date_desc':
          // 期限が遠い順（nullは最後）
          if (!a.due_date) return 1
          if (!b.due_date) return -1
          return new Date(b.due_date).getTime() - new Date(a.due_date).getTime()

        case 'created_at_asc':
          // 作成日が古い順
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()

        case 'created_at_desc':
          // 作成日が新しい順
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()

        case 'custom':
        default:
          // カスタム順（display_order）
          return a.display_order - b.display_order
      }
    })

    return sorted
  }, [todos, selectedProjectId, selectedStatuses, sortOrder, projects, isPersonalSectionVisible, isTeamSectionVisible])

  // ステータス別タスク数はuseStoreから取得（全体のカウント）
  const todoStats = todoCountsByStatus

  // 無限スクロール: Intersection Observerでトリガー要素を監視
  useEffect(() => {
    // 初期ロード中または追加ロード中、またはこれ以上データがない場合は何もしない
    if (loading || loadingMore || !hasMoreTodos) {
      return
    }

    // トリガー要素がまだレンダリングされていない場合は待つ
    if (!loadMoreTriggerRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        try {
          // 最新の状態を取得して確認
          const { loadingMore: currentLoadingMore, hasMoreTodos: currentHasMore, loading: currentLoading } = useStore.getState()

          if (entries[0].isIntersecting && !currentLoadingMore && !currentLoading && currentHasMore) {
            console.log('Intersection Observer: Triggering loadMoreTodos')
            loadMoreTodos()
          }
        } catch (err) {
          console.error('Intersection Observer error:', err)
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px'  // 少し早めに読み込み開始
      }
    )

    observer.observe(loadMoreTriggerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [loading, loadingMore, hasMoreTodos, loadMoreTodos])

  const handleDragStart = async (event: DragStartEvent) => {
    setActiveId(event.active.id as string)

    // ドラッグ開始時にAudioContextを初期化（ユーザー操作内なので確実に起動）
    await initializeFeedback()

    // ドラッグ開始時の要素とポインタの相対位置を記録
    const { active, activatorEvent } = event;
    if (activatorEvent && active.rect.current.translated) {
      let pointerX = 0;
      let pointerY = 0;

      if ('touches' in activatorEvent && (activatorEvent as TouchEvent).touches?.length > 0) {
        const touch = (activatorEvent as TouchEvent).touches[0];
        pointerX = touch.clientX;
        pointerY = touch.clientY;
      } else if ('clientX' in activatorEvent) {
        pointerX = (activatorEvent as MouseEvent).clientX;
        pointerY = (activatorEvent as MouseEvent).clientY;
      }

      // 要素の中心とポインタの位置の差分を計算
      const rect = active.rect.current.translated;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // ネイティブアプリではsafe-area-inset-topを考慮
      // overlaysWebView: true の場合、座標系がステータスバー領域を含むため補正が必要
      let yOffset = -20; // 指の少し上に表示
      if (isNativeApp()) {
        // CSSのsafe-area-inset-topの値を取得
        const safeAreaTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sat') || '0', 10);
        // safe-area-insetが設定されていない場合はCSSから取得を試みる
        const computedSafeArea = getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)');
        if (safeAreaTop > 0) {
          yOffset = -20 - safeAreaTop;
        } else if (computedSafeArea) {
          // iOSでは通常44px程度
          yOffset = -20 - 44;
        }
      }

      setPointerOffset({
        x: pointerX - centerX,
        y: pointerY - centerY + yOffset
      });

      initialPointerRef.current = { x: pointerX, y: pointerY };
    }

    // Safari対策：ドラッグ中のクラス追加のみ
    document.body.classList.add('dragging')
  }

  const handleDragOver = async (event: DragOverEvent) => {
    const { over } = event

    // 新しいタスクの上に来た時だけフィードバック
    if (over && over.id !== lastOverId && over.id !== activeId) {
      setLastOverId(over.id as string)
      // 音でフィードバック
      await triggerFeedback('move', { soundEnabled })
    }
  }

  const handleDragMove = (_event: DragMoveEvent) => {
    // Safari対策：自動スクロールを無効化（フリーズの原因）
    // ユーザーは手動でスクロールしながらドラッグできます
    return
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    // 重複イベント防止: 500ms以内の連続イベントを無視
    const now = Date.now()
    if (now - lastDragEndTimeRef.current < 500) {
      console.log('[DragEnd] Ignoring duplicate event (within 500ms)')
      return
    }
    lastDragEndTimeRef.current = now

    try {
      const { active, over } = event

      if (active.id !== over?.id && over?.id) {
        const oldIndex = filteredAndSortedTodos.findIndex(todo => todo.id === active.id)
        const newIndex = filteredAndSortedTodos.findIndex(todo => todo.id === over.id)

        if (oldIndex !== -1 && newIndex !== -1) {
          // display_orderの重複チェック
          const orderSet = new Set<number>()
          let hasDuplicates = false
          for (const todo of filteredAndSortedTodos) {
            if (orderSet.has(todo.display_order)) {
              hasDuplicates = true
              break
            }
            orderSet.add(todo.display_order)
          }

          if (hasDuplicates) {
            // 重複がある場合: 全タスクの順序を再割り当て
            // dnd-kitのarrayMoveと同じ動作を手動で行う
            const reorderedTodos = [...filteredAndSortedTodos]
            const [movedItem] = reorderedTodos.splice(oldIndex, 1)
            reorderedTodos.splice(newIndex, 0, movedItem)

            // 新しい順序を1000刻みで割り当て
            const todoIds = reorderedTodos.map(t => t.id)
            const newOrders = reorderedTodos.map((_, idx) => idx * 1000)

            // バッチ更新
            await updateTodosOrderBatch(todoIds, newOrders)
          } else {
            // 重複がない場合: Fractional Indexing
            // overの位置に挿入するためのnewOrderを計算
            // 下に移動する場合: over要素の「後」に配置
            // 上に移動する場合: over要素の「前」に配置
            let newOrder: number

            if (oldIndex < newIndex) {
              // 下に移動: over要素とその次の要素の間に配置
              const targetOrder = filteredAndSortedTodos[newIndex]?.display_order ?? 0
              const nextOrder = filteredAndSortedTodos[newIndex + 1]?.display_order ?? targetOrder + 2000
              newOrder = Math.floor((targetOrder + nextOrder) / 2)
            } else {
              // 上に移動: over要素の前の要素とover要素の間に配置
              const targetOrder = filteredAndSortedTodos[newIndex]?.display_order ?? 0
              const prevOrder = filteredAndSortedTodos[newIndex - 1]?.display_order ?? targetOrder - 2000
              newOrder = Math.floor((prevOrder + targetOrder) / 2)
            }

            await updateTodoOrder(active.id as string, newOrder)
          }

          // 効果音フィードバック
          triggerFeedback('move', { soundEnabled })
        }
      }
    } catch (err) {
      console.error('Drag end error:', err)
    } finally {
      // 必ずクリーンアップを実行
      setActiveId(null)
      setLastOverId(null)
      setPointerOffset({ x: 0, y: 0 })
      initialPointerRef.current = null

      // クリーンアップ
      stopAutoScroll()
      cleanupDrag()
    }
  }

  const handleDragCancel = () => {
    // Safari用：ドラッグがキャンセルされた場合のクリーンアップ
    setActiveId(null)
    setLastOverId(null)
    setPointerOffset({ x: 0, y: 0 })
    initialPointerRef.current = null
    stopAutoScroll()
    cleanupDrag()
  }

  const activeTodo = activeId ? todos.find(t => t.id === activeId) : null

  // fullモードのときはGanttChartV2が独自のフルスクリーン処理を持っているため、
  // GanttChartV2Pageで処理される。TodoList内での特別処理は不要。

  return (
    <div className="flex flex-col h-full">
      {/* Desktop Upgrade Banner */}
      <div className="flex-shrink-0">
        <UpgradeBanner variant="desktop" />
      </div>

      <div className="bg-white rounded-lg shadow-sm border flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* ヘッダー部分は固定 */}
        <div className="p-1.5 sm:p-3 border-b flex-shrink-0">
          {/* モバイル：1行にコンパクト表示 */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                {selectedProject && (
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedProject.color }}
                  />
                )}
                <span className="text-sm font-semibold truncate" style={{ color: selectedProject?.color }}>
                  {selectedProject ? selectedProject.name : t('todo.all')}
                </span>
                <div className="flex gap-1 text-[10px] text-gray-600 flex-shrink-0">
                  <span>{t('todo.total')}{todoStats.total}</span>
                  <span className="text-yellow-600">{t('todo.pending')}{todoStats.pending}</span>
                  <span className="text-blue-600">{t('todo.inProgress')}{todoStats.in_progress}</span>
                  <span className="text-green-600">{t('todo.completed')}{todoStats.completed}</span>
                </div>
              </div>
              <div className="flex gap-1 items-center">
                {/* フィルターボタン */}
                <div className="relative" data-tour="filter-mobile">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className="px-1 py-1 text-[10px] border rounded bg-white border-gray-300 flex items-center gap-1"
                  >
                    <Filter className="h-3 w-3" />
                    {selectedStatuses.length === 4 ? t('todo.showAll') : t('todo.showFiltered', { count: selectedStatuses.length })}
                  </button>
                {showFilterMenu && (
                  <div className="absolute right-0 mt-1 bg-white border rounded shadow-lg p-2 z-50 min-w-[100px]">
                    {[
                      { value: 'pending' as FilterStatus, label: t('todo.statusPending') },
                      { value: 'in_progress' as FilterStatus, label: t('todo.statusInProgress') },
                      { value: 'completed' as FilterStatus, label: t('todo.statusCompleted') },
                      { value: 'deleted' as FilterStatus, label: 'ゴミ箱' }
                    ].map(status => (
                      <label key={status.value} className="flex items-center gap-1 text-xs py-1 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // ゴミ箱を選択した場合は他のステータスを非表示
                              if (status.value === 'deleted') {
                                setSelectedStatuses(['deleted'])
                              } else {
                                // 他のステータスを選択した場合はゴミ箱を除外
                                const newStatuses = [...selectedStatuses.filter(s => s !== 'deleted'), status.value]
                                setSelectedStatuses(newStatuses)
                              }
                            } else {
                              const newStatuses = selectedStatuses.filter(s => s !== status.value)
                              if (newStatuses.length > 0) {
                                setSelectedStatuses(newStatuses)
                              }
                            }
                          }}
                          className="rounded"
                        />
                        {status.label}
                      </label>
                    ))}
                  </div>
                )}
                </div>

                {/* ソートボタン */}
                <div className="relative" data-tour="sort-mobile">
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="px-1.5 py-1 text-[11px] border rounded bg-white border-gray-300 flex items-center gap-1"
                  >
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                  {showSortMenu && (
                    <div className="absolute right-0 mt-1 bg-white border rounded shadow-lg py-1 z-50 min-w-[140px]">
                      {[
                        { value: 'custom' as SortOrder, label: 'カスタム順' },
                        { value: 'due_date_asc' as SortOrder, label: '期限近い順' },
                        { value: 'due_date_desc' as SortOrder, label: '期限遠い順' },
                        { value: 'created_at_desc' as SortOrder, label: '作成新しい順' },
                        { value: 'created_at_asc' as SortOrder, label: '作成古い順' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortOrder(option.value)
                            setShowSortMenu(false)
                          }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                            sortOrder === option.value ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* モバイル: ビュー切替タブ */}
          <div className="flex sm:hidden items-center mt-1">
            <div className="flex items-center bg-indigo-100 rounded-lg p-0.5 flex-1">
              {[
                { value: 'tasks' as const, label: 'タスク', icon: ListTodo },
                { value: 'calendar' as const, label: 'カレンダー', icon: Calendar },
                { value: 'gantt' as const, label: 'ガント', icon: BarChart3 },
                { value: 'activity' as const, label: '活動', icon: Activity }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleTabChange(option.value)}
                  className={`flex-1 flex items-center justify-center gap-0.5 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-colors ${
                    activeTab === option.value
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-indigo-700 hover:bg-indigo-200'
                  }`}
                >
                  <option.icon className="w-3.5 h-3.5" />
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* モバイル: タスクビュー時のAddTodoForm + エクスポート（ヘッダー内に固定） */}
          {activeTab === 'tasks' && !isViewer && (
            <div className="sm:hidden flex items-center gap-1 mt-1 pr-1">
              <div className="flex-1"><AddTodoForm /></div>
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('export.button')}
                >
                  <Download className="w-5 h-5" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                      {(['markdown', 'csv', 'json'] as ExportFormat[]).map((format) => (
                        <button
                          key={format}
                          onClick={() => handleExport(format)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {formatLabels[format]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* PC：既存レイアウト */}
          <div className="hidden sm:block">
            {/* プロジェクト名 */}
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              {selectedProject && (
                <div
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded-full mr-2"
                  style={{ backgroundColor: selectedProject.color }}
                />
              )}
              <ListTodo className="h-4 w-4 sm:h-5 sm:w-5 mr-1" style={{ color: selectedProject?.color }} />
              <span className="truncate" style={{ color: selectedProject?.color }}>
                {selectedProject ? selectedProject.name : t('todo.all')}
              </span>
            </h2>
            {/* タブ + 統計 + フィルター/ソート/エクスポート（同じ行） */}
            <div className="mt-1 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center bg-indigo-100 rounded-lg p-0.5">
                  {[
                    { value: 'tasks' as const, label: 'タスク', icon: ListTodo },
                    { value: 'calendar' as const, label: 'カレンダー', icon: Calendar },
                    { value: 'gantt' as const, label: 'ガント', icon: BarChart3 },
                    { value: 'activity' as const, label: '活動ログ', icon: Activity }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleTabChange(option.value)}
                      className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        activeTab === option.value
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'text-indigo-700 hover:bg-indigo-200'
                      }`}
                    >
                      <option.icon className="w-3.5 h-3.5" />
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 sm:gap-3 text-xs text-gray-600">
                  <span>{t('todo.total')}{todoStats.total}</span>
                  <span className="text-yellow-600">{t('todo.pending')}{todoStats.pending}</span>
                  <span className="text-blue-600">{t('todo.inProgress')}{todoStats.in_progress}</span>
                  <span className="text-green-600">{t('todo.completed')}{todoStats.completed}</span>
                </div>
              </div>

              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-1 relative" data-tour="filter">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className="px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white flex items-center gap-1"
                  >
                    <Filter className="h-3 w-3 text-gray-500" />
                    {selectedStatuses.length === 4 ? t('todo.showAll') : t('todo.showFiltered', { count: selectedStatuses.length })}
                  </button>
                  {showFilterMenu && (
                    <div className="absolute right-0 mt-1 top-full bg-white border rounded shadow-lg p-2 z-50 min-w-[120px]">
                      {[
                        { value: 'pending' as FilterStatus, label: t('todo.statusPending') },
                        { value: 'in_progress' as FilterStatus, label: t('todo.statusInProgress') },
                        { value: 'completed' as FilterStatus, label: t('todo.statusCompleted') },
                        { value: 'deleted' as FilterStatus, label: 'ゴミ箱' }
                      ].map(status => (
                        <label key={status.value} className="flex items-center gap-2 text-sm py-1.5 cursor-pointer hover:bg-gray-50 px-1 rounded">
                          <input
                            type="checkbox"
                            checked={selectedStatuses.includes(status.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // ゴミ箱を選択した場合は他のステータスを非表示
                                if (status.value === 'deleted') {
                                  setSelectedStatuses(['deleted'])
                                } else {
                                  // 他のステータスを選択した場合はゴミ箱を除外
                                  const newStatuses = [...selectedStatuses.filter(s => s !== 'deleted'), status.value]
                                  setSelectedStatuses(newStatuses)
                                }
                              } else {
                                const newStatuses = selectedStatuses.filter(s => s !== status.value)
                                if (newStatuses.length > 0) {
                                  setSelectedStatuses(newStatuses)
                                }
                              }
                            }}
                            className="rounded"
                          />
                          {status.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* ソートボタン - タスクビューのみ表示 */}
                {activeTab === 'tasks' && (
                  <>
                    <div className="flex items-center gap-1 relative" data-tour="sort">
                      <button
                        onClick={() => setShowSortMenu(!showSortMenu)}
                        className="px-2.5 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white flex items-center gap-1.5"
                      >
                        <ArrowUpDown className="h-4 w-4 text-gray-500" />
                        <span className="hidden sm:inline">
                          {sortOrder === 'custom' && '並び順'}
                          {sortOrder === 'due_date_asc' && '期限近い順'}
                          {sortOrder === 'due_date_desc' && '期限遠い順'}
                          {sortOrder === 'created_at_asc' && '作成古い順'}
                          {sortOrder === 'created_at_desc' && '作成新しい順'}
                        </span>
                      </button>
                      {showSortMenu && (
                        <div className="absolute right-0 mt-1 top-full bg-white border rounded shadow-lg py-1 z-50 min-w-[150px]">
                          {[
                            { value: 'custom' as SortOrder, label: 'カスタム順', icon: GripVertical },
                            { value: 'due_date_asc' as SortOrder, label: '期限が近い順' },
                            { value: 'due_date_desc' as SortOrder, label: '期限が遠い順' },
                            { value: 'created_at_desc' as SortOrder, label: '作成日が新しい順' },
                            { value: 'created_at_asc' as SortOrder, label: '作成日が古い順' }
                          ].map(option => (
                            <button
                              key={option.value}
                              onClick={() => {
                                setSortOrder(option.value)
                                setShowSortMenu(false)
                              }}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                                sortOrder === option.value ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {option.icon && <option.icon className="h-4 w-4" />}
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {sortOrder === 'custom' && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <GripVertical className="h-3 w-3" />
                        <span>{t('todo.dragToReorder')}</span>
                      </div>
                    )}
                  </>
                )}

                {/* PC用エクスポートボタン */}
                <div className="relative" ref={exportMenuRef}>
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    title={t('export.button')}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="py-1">
                        {(['markdown', 'csv', 'json'] as ExportFormat[]).map((format) => (
                          <button
                            key={format}
                            onClick={() => handleExport(format)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            {formatLabels[format]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* GanttChartV2は独自のズーム・全画面コントロールを持つため、ここでは不要 */}
              </div>
            </div>
          </div>
        </div>

        {/* タスクビュー（タスク独自のスワイプ操作があるためページスワイプ無効） */}
        {activeTab === 'tasks' && (
          <div
            className="flex-1 flex flex-col min-h-0 overflow-hidden"
          >
            {/* PC用: AddTodoFormは固定 */}
            {!isViewer && (
              <div className="hidden sm:block flex-shrink-0">
                <AddTodoForm />
              </div>
            )}

            {/* タスクリストのみスクロール（縦のみ、横スクロール無効） */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 sm:p-0 bg-white drag-container" style={{ touchAction: 'pan-y' }} data-tour="task-list">
              {filteredAndSortedTodos.length === 0 ? (
            <div className="p-8 text-center text-gray-500 bg-white rounded-lg sm:rounded-none">
              {selectedStatuses.length === 3
                ? t('todo.noTasksYet')
                : t('todo.noTasksInFilter')
              }
            </div>
          ) : sortOrder === 'custom' && !isProjectSelectorOpen && !isViewer ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={filteredAndSortedTodos.map(t => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredAndSortedTodos.map(todo => (
                  <SortableTodoItem key={todo.id} todo={todo} onTriggerCelebration={triggerCelebration} />
                ))}
              </SortableContext>
              <DragOverlay
                dropAnimation={{
                  sideEffects: defaultDropAnimationSideEffects({
                    styles: {
                      active: {
                        opacity: '1',
                      },
                    },
                  }),
                }}
                modifiers={[cursorOffsetModifier]}
                style={{
                  touchAction: 'none',
                  cursor: 'grabbing',
                }}
              >
                {activeTodo ? (
                  <div style={{
                    opacity: 0.95,
                    transform: 'scale(1.05)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.25)',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                  }}>
                    <TodoItem todo={activeTodo} />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <>
              {filteredAndSortedTodos.map(todo => (
                <TodoItem key={todo.id} todo={todo} onTriggerCelebration={triggerCelebration} />
              ))}
            </>
          )}

          {/* プロジェクト選択画面が開いている場合の警告（開発用） */}
          {isProjectSelectorOpen && sortOrder === 'custom' && (
            <div className="hidden">Task drag disabled: project selector is open</div>
          )}

          {/* 無限スクロール: ローディング表示とトリガー要素 */}
          {filteredAndSortedTodos.length > 0 && hasMoreTodos && (
            <div ref={loadMoreTriggerRef} className="p-4 flex justify-center items-center">
              {loadingMore ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">{t('todo.loadingMore')}</span>
                </div>
              ) : (
                <div className="text-xs text-gray-400">{t('todo.scrollToLoadMore')}</div>
              )}
            </div>
          )}

          {/* すべて読み込み完了メッセージ */}
          {filteredAndSortedTodos.length > 0 && !hasMoreTodos && (
            <div className="p-4 text-center text-xs text-gray-400 border border-gray-300 rounded-lg mx-2 sm:mx-0 mt-2 bg-white">
              {t('todo.allTasksLoaded', { count: filteredAndSortedTodos.length })}
            </div>
          )}
            </div>
          </div>
        )}

        {/* 活動ログビュー */}
        {activeTab === 'activity' && (
          <div className="flex-1 overflow-y-auto p-4">
            {selectedProject ? (
              <ProjectActivityLog
                projectId={selectedProject.id}
                onSwitchToTasks={() => handleTabChange('tasks')}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Activity className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-sm">
                  活動ログはプロジェクトごとに記録されます。
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  プロジェクトを選択して、<br />活動ログを閲覧してください。
                </p>
              </div>
            )}
          </div>
        )}

        {/* カレンダービュー */}
        {activeTab === 'calendar' && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <CalendarView projectId={selectedProjectId} filterStatuses={selectedStatuses} />
          </div>
        )}

        {/* ガントチャートビュー V2（横スクロールがあるためページスワイプ無効） */}
        {activeTab === 'gantt' && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <GanttChartV2 filterStatuses={selectedStatuses} />
          </div>
        )}
      </div>

      {/* Celebration Effects */}
      {celebrations.map(celebration => (
        <CelebrationEffect
          key={celebration.id}
          centerX={celebration.x}
          centerY={celebration.y}
          onComplete={() => removeCelebration(celebration.id)}
        />
      ))}
    </div>
  )
}